import { z } from "zod";

import { Locale } from "./locale.js";
import { type LocalizedText } from "./localizedText.js";
import { checkProgramModel, ProgramModelShape } from "./programModel.js";
import { normalizeSearch } from "./search.js";
import { PlanTemplate, ProgramTemplate } from "./templateSchema.js";

// A public template as stored in DDB (#344): the template content plus who
// wrote it, in which language, and who may see it. One row per template at
// pk=TEMPLATE, sk=PROGRAM#<id> | PLAN#<id> | MODEL#<id> (a program model, #351).

/** The author of the official templates. A coach's sub goes here later. */
export const METRIA_AUTHOR = "metria";

/** Only free today; a paid template waits for billing (#126). */
export const TemplateAccess = z.enum(["free"]);
export type TemplateAccess = z.infer<typeof TemplateAccess>;

const rowMeta = {
  author: z.string().min(1).max(128),
  authorLocale: Locale,
  access: TemplateAccess,
  hidden: z.boolean().default(false),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
};

export const ProgramTemplateRow = ProgramTemplate.extend(rowMeta);
export type ProgramTemplateRow = z.infer<typeof ProgramTemplateRow>;
export const PlanTemplateRow = PlanTemplate.extend(rowMeta);
export type PlanTemplateRow = z.infer<typeof PlanTemplateRow>;
export const ProgramModelRow = ProgramModelShape.extend(rowMeta).superRefine(checkProgramModel);
export type ProgramModelRow = z.infer<typeof ProgramModelRow>;

// The body of an admin write: the content, plus what the admin may set.
// The server stamps author, authorLocale and the dates. The id becomes the
// sort key and a URL segment, so a write takes only a lowercase slug; the
// read schema keeps the looser id, so no stored row stops parsing.
const TemplateId = z
  .string()
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

const writeMeta = {
  id: TemplateId,
  hidden: z.boolean().optional(),
  access: TemplateAccess.optional(),
};

export const ProgramTemplateWrite = ProgramTemplate.extend(writeMeta);
export type ProgramTemplateWrite = z.infer<typeof ProgramTemplateWrite>;
export const PlanTemplateWrite = PlanTemplate.extend(writeMeta);
export type PlanTemplateWrite = z.infer<typeof PlanTemplateWrite>;
export const ProgramModelWrite = ProgramModelShape.extend(writeMeta).superRefine(checkProgramModel);
export type ProgramModelWrite = z.infer<typeof ProgramModelWrite>;

/** The protected names, one row at pk=TEMPLATE, sk=RESERVEDNAMES. */
export const ReservedNames = z.object({
  names: z.array(z.string().trim().min(1).max(200)).max(1000),
});
export type ReservedNames = z.infer<typeof ReservedNames>;

/** Case, accents, compatibility forms and punctuation do not count:
 *  every run of non-letters and non-digits is one space. Stricter than
 *  normalizeSearch on purpose: slashes or colons cannot dodge a reserved name. */
export const normalizeReservedName = (s: string): string =>
  normalizeSearch(s).replace(/[^\p{L}\p{N}]+/gu, " ").trim();

/** The first reserved name that one of `name`'s languages contains, or null.
 *  The list protects Metria's names from everyone else: a Metria template is
 *  never checked. */
export const reservedNameHit = (
  name: LocalizedText,
  reserved: readonly string[],
  author: string,
): { reserved: string; locale: Locale } | null => {
  if (author === METRIA_AUTHOR) return null;
  const own = Object.entries(name)
    .filter((e): e is [string, string] => typeof e[1] === "string")
    .map(([locale, text]) => [locale as Locale, normalizeReservedName(text)] as const);
  for (const entry of reserved) {
    const needle = normalizeReservedName(entry);
    if (needle.length === 0) continue;
    const hit = own.find(([, text]) => text.includes(needle));
    if (hit) return { reserved: entry, locale: hit[0] };
  }
  return null;
};
